import React, { useEffect, useState, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams, useNavigate } from "react-router-dom"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { Button, Card, CardBody, Col, Row, Spinner } from "reactstrap"
import disputeImg1 from "../../../assets/images/disputes/dispute-1.png"
import DisputeHistory from "./components/DisputeHistory"
import { ChangeStatusDiputeModal } from "./Modals"
import { useTranslation } from "react-i18next"
import { getDispute, changeDisputeStatus } from "../../../store/disputes/actions"
import { BackendDispute, Dispute } from "./Disputes"

// Helper function to format date to '23 jun, 2022' format
const formatDate = (dateString: string): string => {
  if (!dateString) return "__"
  const date = new Date(dateString)
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
  const day = date.getDate()
  const month = months[date.getMonth()]
  const year = date.getFullYear()
  return `${day} ${month}, ${year}`
}

// Helper function to format date with time
const formatDateTime = (dateString: string): string => {
  if (!dateString) return "__"
  const date = new Date(dateString)
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
  const day = date.getDate()
  const month = months[date.getMonth()]
  const year = date.getFullYear()
  const hours = date.getHours()
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const formattedHours = hours % 12 || 12
  return `${day} ${month}, ${year} at ${formattedHours}:${minutes} ${ampm}`
}

const DisputeDetails = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Get dispute from Redux store
  const { selectedDispute, loading } = useSelector((state: any) => state.Disputes)

  // Fetch dispute on mount
  useEffect(() => {
    if (id) {
      dispatch(getDispute(Number(id), { push: navigate }))
    }
  }, [id, dispatch, navigate])

  // Map backend dispute to frontend format
  const disputeDetails = useMemo(() => {
    if (!selectedDispute) {
      return null
    }

    return {
      id: `ID-${selectedDispute.id}`,
      title: selectedDispute.title || "__",
      createdAt: formatDate(selectedDispute.issueTime),
      createdBy: selectedDispute.issuedByAccount?.fullName || selectedDispute.issuedByAccount?.username || "__",
      createdPhoto: "https://placehold.co/30x30/png",
      description: selectedDispute.claims || "__",
      image: (() => {
        const docs = selectedDispute.documents?.map((d: any) => d.url || disputeImg1) || []
        return docs.length > 0 ? docs : [disputeImg1]
      })(),
      disputeHistory: (selectedDispute.logs || []).map((log: any) => ({
        date: formatDateTime(log.logTime),
        status: log.actionName || "__",
        notes: log.notes || "",
      })) || [
        // Default history if no logs
        {
          date: formatDateTime(selectedDispute.issueTime),
          status: "Dispute created",
          notes: "",
        },
      ],
    }
  }, [selectedDispute])

  const [copied, setCopied] = useState(false)
  const [openAccordion, setOpenAccordion] = useState(true)
  const [openChangeStatusModal, setOpenChangeStatusModal] = useState<{
    open: boolean
    view: "respond" | "rejected"
    dispute: Dispute | null
  }>({
    open: false,
    view: "respond",
    dispute: null,
  })

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  const approveRefundHandler = () => {
    if (!id) return
    // Approve refund by changing dispute status to Resolved (statusId: 3)
    const statusData = {
      statusId: 3,
      actionId: 3,
      notes: "Resolved",
      isInternal: true,
    }
    dispatch(changeDisputeStatus(Number(id), statusData, { push: navigate }))
  }

  const rejectHandler = () => {
    if (!disputeDetails || !id) return
    // Open the reject modal with the current dispute
    setOpenChangeStatusModal({
      open: true,
      view: "rejected",
      dispute: {
        id: id, // Use numeric ID from URL, not "ID-123" format
        title: disputeDetails.title,
        createdDate: disputeDetails.createdAt,
        createdBy: disputeDetails.createdBy,
        attachments: disputeDetails.image,
        status: "disputes_pending" as any,
        description: disputeDetails.description,
      },
    })
  }

  const respondHandler = () => {
    if (!disputeDetails || !id) return
    // Open the respond modal with the current dispute
    setOpenChangeStatusModal({
      open: true,
      view: "respond",
      dispute: {
        id: id, // Use numeric ID from URL, not "ID-123" format
        title: disputeDetails.title,
        createdDate: disputeDetails.createdAt,
        createdBy: disputeDetails.createdBy,
        attachments: disputeDetails.image,
        status: "disputes_pending" as any,
        description: disputeDetails.description,
      },
    })
  }

  // Show loading state if we have an ID but no dispute details yet
  // OR if explicitly loading from Redux
  if (loading || (id && !disputeDetails)) {
    return (
      <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner color="primary" />
      </div>
    )
  }

  // Show error state if dispute not found (and we're not loading)
  if (!disputeDetails) {
    return (
      <div className="page-content d-flex justify-content-center align-items-center">
        <p>{t("disputes_disputeNotFound")}</p>
      </div>
    )
  }

  return (
    <div className="page-content">
      <div className="container-fluid d-flex justify-content-between align-items-center">
        <Breadcrumbs
          title={t("dashboard_dashboard")}
          breadcrumbItem={t("disputes_disputes")}
        />
        <div className="d-flex gap-2 align-items-center">
          {/* <Button
            onClick={() => {}}
            className="bg-gradiant-blue border-0 text-capitalize"
          >
            {t("disputes_forward")}
          </Button>
          <Button
            onClick={() => {}}
            className="bg-white border-1 text-primary border-primary"
          >
            {t("disputes_escalated")}
          </Button> */}
          {selectedDispute?.statusId === 2 && (
            <span className="fw-regular text-danger">
              {t("disputes_escalatedToNartawi")}
            </span>
          )}
          <Button
            onClick={rejectHandler}
            className="bg-transparent border text-danger border-danger text-capitalize"
          >
            {t("disputes_rejectDispute")}
          </Button>
        </div>
      </div>
      <Card className="shadow-md rounded-3">
        <CardBody>
          <Row className="justify-content-between">
            <Col md={4} className="d-flex flex-column align-items-start gap-1">
              <h4 className="fw-bold medium text-capitalize mb-0">
                {disputeDetails.title}
              </h4>
              <div className="d-flex align-items-center gap-2">
                <p className="text-muted m-0">
                  <span className="fw-semibold font-size-14 text-semi-dark">
                    {t("disputes_disputeID")}:
                  </span>
                  <span className="mx-2">{disputeDetails.id}</span>
                </p>
                <button
                  className="relative cursor-pointer border-0 bg-transparent"
                  onClick={() => {
                    copyToClipboard(disputeDetails.id)
                  }}
                >
                  <i className="bx bx-copy px-2 text-primary bx-sm cursor-pointer"></i>
                  {copied && (
                    <span
                      className="position-absolute  translate-middle-x mt-1 text-success small"
                      style={{ top: "14px", marginLeft: "20px" }}
                    >
                      {t("common_copied")}
                    </span>
                  )}
                </button>
              </div>
            </Col>
            <Col md={4}>
              <p className="text-muted my-1">
                <span className="fw-semibold font-size-14 text-semi-dark">
                  {t("common_createdAt")}:
                </span>
                <span className="mx-2">{disputeDetails.createdAt}</span>
              </p>
              <p className="text-muted m-0">
                <span className="fw-semibold font-size-14 text-semi-dark">
                  {t("common_createdBy")}:
                </span>
                <span className="mx-2">
                  <img
                    src={disputeDetails.createdPhoto}
                    alt=""
                    className="rounded-circle mx-2"
                  />
                  {disputeDetails.createdBy}
                </span>
              </p>
            </Col>
            <Col
              md={4}
              className="d-flex flex-column align-items-start gap-1 justify-content-center align-items-end"
            >
              <Button
                onClick={approveRefundHandler}
                className="bg-gradiant-blue border-0 text-capitalize w-50"
              >
                {t("disputes_approveRefund")}
              </Button>
              <Button
                onClick={respondHandler}
                className="bg-white border-1 text-primary border-primary w-50"
              >
                {t("disputes_respondToDispute")}
              </Button>
            </Col>
          </Row>
          <p className="border-bottom border-top p-2 mt-4">
            {disputeDetails.description}
          </p>
          <div className="mt-2">
            <h5 className="fw-semibold font-size-14 text-semi-dark">
              {t("common_attachments")}:
            </h5>
            {disputeDetails.image.map((image: string, index: number) => (
              <img
                key={index}
                src={image}
                alt=""
                className="m-2"
                style={{
                  maxWidth: "200px",
                  maxHeight: "200px",
                  objectFit: "cover",
                }}
                onError={e => {
                  const target = e.target as HTMLImageElement
                  target.src = disputeImg1
                }}
              />
            ))}
          </div>
        </CardBody>
      </Card>

      <Card className="shadow-md rounded-3 mt-2">
        <DisputeHistory
          openAccordion={openAccordion}
          setOpenAccordion={setOpenAccordion}
          disputeHistory={disputeDetails.disputeHistory}
        />
      </Card>

      {/* Change Status Dispute Modal */}
      <ChangeStatusDiputeModal
        currentView={openChangeStatusModal.view}
        isOpen={openChangeStatusModal.open}
        dispute={openChangeStatusModal.dispute}
        toggle={() =>
          setOpenChangeStatusModal({
            open: false,
            view: "respond",
            dispute: null,
          })
        }
      />
    </div>
  )
}

export default DisputeDetails
